import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { apiRequest } from "src/async/apiUtils";
import Button from "src/components/form/Button";
import Preloader from "src/components/shared/LoadingAnimations/Preloader";
import { SiteContext } from "src/context/site";
import AuthDataLayout from "src/layouts/AuthDataLayout";
import ResetPasswordForm from "./ResetPasswordForm";
import ResetPasswordUrlInvalid from "./ResetPasswordUrlInvalid";
import { useAsyncAction } from "src/hooks/useAsyncAction";

const ResetPassword = ({ type = "welcome-mail" }) => {
  const navigate = useNavigate();
  const { token } = useParams();

  const { loading: siteLoading, site } = useContext(SiteContext);

  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const passwordValidation = !password || !confirmPassword || password !== confirmPassword || !/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[\W_])[^\s]{8,}$/.test(password);

  const saveTokenAndLogin = (resData) => {
    localStorage.setItem("email", resData?.email);
    localStorage.setItem("accessToken", resData.access_token || resData.accessToken);
    localStorage.setItem("refreshToken", resData.refresh_token || resData.refreshToken);
    window.postMessage(
      {
        type: "LOGIN_SUCCESS",
        token: resData.access_token,
      },
      "*",
    );

    window.location.href = "/";
  };

  useEffect(() => {
    const verifyToken = async () => {
      setLoading(true);

      try {
        const { data: resData } = await apiRequest("post", "/portal/verify-token", { body: { token } });

        if (resData.status === 200) {
          setVerified(true);

          setPassword("");
          setConfirmPassword("");
        } else {
          setVerified(false);
        }

        setLoading(false);
      } catch (err) {
        setVerified(false);

        setLoading(false);
      } finally {
      }
    };

    verifyToken();
  }, [token, type]);

  const {
    loading: resetLoading,
    success: resetSuccess,
    execute: resetPasswordAction,
  } = useAsyncAction({
    onSuccess: (res) => {
      const resData = res.data;
      if (resData.status === 200) {
        if (type === "welcome-mail") {
          saveTokenAndLogin(resData);
        } else {
          navigate("/login");
        }
      } else {
        toast.error(resData.message);
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const onSubmit = async () => {
    resetPasswordAction(async () => {
      return await apiRequest("post", "/portal/change-password", {
        body: { password, confirm_password: confirmPassword, token },
      });
    });
  };

  if (loading || siteLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Preloader />
      </div>
    );
  }

  const LinkUpComponent = () => (
    <div className="mt-4">
      <div className="flex justify-center space-x-2 font-medium">
        <span>Return to</span>
        <Button
          buttonClasses={"!px-0 !h-auto !bg-transparent !rounded-none"}
          buttonHasLink={true}
          buttonLink={"/login"}
          buttonLabel={"Login page"}
          buttonLabelClasses={"hover:!underline !text-highlightColor"}
        />
      </div>
    </div>
  );

  return (
    <>
      {!loading && !verified ? (
        <ResetPasswordUrlInvalid url="/forgot-password" />
      ) : (
        <AuthDataLayout
          title="Create a new password"
          subTitle="Enter your new password and let's get the ball rolling!"
          LinkUpComponent={LinkUpComponent}>
          <form
            className="flex w-full flex-col"
            id="loginform">
            <ResetPasswordForm
              password={password}
              confirmPassword={confirmPassword}
              setPassword={setPassword}
              setConfirmPassword={setConfirmPassword}
            />
            <div className="mt-2 grid gap-y-4">
              <Button
                isDisable={resetLoading || passwordValidation}
                loading={resetLoading}
                success={resetSuccess}
                buttonClasses={"w-full"}
                buttonStyles={{
                  borderColor: site?.highlight_color,
                  backgroundColor: site?.highlight_color,
                }}
                buttonType={"submit"}
                buttonLabel={type === "welcome-mail" ? "Create New Account" : "Reset Password"}
                buttonLabelClasses={""}
                buttonFunction={(e) => {
                  e.preventDefault();
                  onSubmit();
                }}
              />
            </div>
          </form>
        </AuthDataLayout>
      )}
    </>
  );
};

export default ResetPassword;
